/**
 * The employee's own enrollment photo, used wherever an attendance table names a person.
 *
 * Shared by the directory and the presence board rather than living in either: both render
 * the same 36px circle from the same `primary_photo_id` the command-center puts on their
 * rows, and one cache across the two means switching tabs doesn't re-download the roster's
 * faces.
 */
import { useState, useEffect } from 'react';
import { useApi } from '../../api';
import { Avatar } from '../../components/alerts/Avatar';

/**
 * Enrollment photos already fetched, keyed by photo id, so paging back to a page you have
 * already seen doesn't re-download 50 faces. Bounded: these are base64 data URLs of full
 * photographs, and an unbounded map over a 500-person directory is tens of megabytes held
 * for the life of the tab. Ids are immutable — a replaced photo is a new row with a new id —
 * so a cached entry can never go stale.
 */
const PHOTO_CACHE = new Map<string, string | null>();
const PHOTO_CACHE_MAX = 150;

function cachePhoto(id: string, url: string | null) {
  PHOTO_CACHE.set(id, url);
  while (PHOTO_CACHE.size > PHOTO_CACHE_MAX) {
    // Map iterates in insertion order, so the first key is the oldest.
    const oldest = PHOTO_CACHE.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    PHOTO_CACHE.delete(oldest);
  }
}

/**
 * The person's own face where one is enrolled, falling back to the initials bubble.
 *
 * Recognising a colleague by their photo is faster than reading their name, and in the
 * directory it is also the answer to "is the face on file the right one?" — a wrong or
 * unusable enrollment photo is invisible in a 3/5 pip meter. Rows carry only the photo's id,
 * so the image is fetched per row; the failure case (deleted object, proxy error, a backend
 * too old to send the id at all) resolves to null and simply shows initials.
 */
function EmployeeAvatar({ photoId, name }: { photoId?: string | null; name?: string | null }) {
  const { attendance } = useApi();
  const [src, setSrc] = useState<string | null>(() => (photoId ? PHOTO_CACHE.get(photoId) ?? null : null));

  useEffect(() => {
    if (!photoId) { setSrc(null); return; }
    if (PHOTO_CACHE.has(photoId)) { setSrc(PHOTO_CACHE.get(photoId) ?? null); return; }
    let alive = true;
    attendance.employeePhotoDataUrl(photoId)
      .then((u) => { cachePhoto(photoId, u); if (alive) setSrc(u); })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; };
  }, [attendance, photoId]);

  if (!src) return <Avatar name={name} className="h-9 w-9 text-[11px]" />;
  return (
    <img
      src={src}
      alt=""
      title={name || undefined}
      // Same 36px circle as the initials bubble it replaces, so a page where only some
      // people have photos still reads as one column of rows rather than two.
      className="h-9 w-9 flex-shrink-0 rounded-full border border-border object-cover"
    />
  );
}

export default EmployeeAvatar;
